import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, ArrowLeft, ArrowRight, CheckCircle, Shield, KeyRound, AlertTriangle } from 'lucide-react';
import { authAPI } from '../lib/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate password strength
    if (password.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }
    
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    
    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    
    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number');
      return;
    }
    
    setLoading(true);
    
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    }
    setLoading(false);
  };

  // No token provided
  if (!token) {
    return (
      <div className="login-container" data-testid="reset-password-page">
        <div
          className="login-left"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1765062165737-ed4b47014fef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc3MDgxODIzMXww&ixlib=rb-4.1.0&q=85)` }}
        >
          <div className="relative z-10 flex flex-col justify-end h-full p-12">
            <h2
              className="text-white text-5xl font-bold leading-tight mb-4"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              INVALID<br />LINK
            </h2>
            <p className="text-slate-300 text-base max-w-md leading-relaxed">
              The password reset link appears to be invalid or expired.
            </p>
          </div>
        </div>

        <div className="login-right">
          <div className="w-full max-w-sm text-center">
            <div className="flex items-center gap-3 mb-10 justify-center">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, hsl(221, 83%, 53%), hsl(221, 83%, 42%))", boxShadow: "0 4px 14px rgba(37, 99, 235, 0.25)" }}>
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                  SIMLABDT
                </h1>
                <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Manager</span>
              </div>
            </div>

            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            
            <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              Invalid Reset Link
            </h3>
            <p className="text-slate-500 text-sm mb-8">
              This password reset link is invalid or has expired. Please request a new one.
            </p>

            <div className="space-y-3">
              <Link to="/forgot-password">
                <Button className="w-full btn-primary rounded-lg h-11" data-testid="request-new-link-btn">
                  Request New Link
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="w-full rounded-lg border-slate-200 h-11" data-testid="back-to-login-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container" data-testid="reset-password-page">
      {/* Left - Image */}
      <div
        className="login-left"
        style={{ backgroundImage: `url(https://images.unsplash.com/photo-1765062165737-ed4b47014fef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc3MDgxODIzMXww&ixlib=rb-4.1.0&q=85)` }}
      >
        <div className="relative z-10 flex flex-col justify-end h-full p-12">
          <h2
            className="text-white text-5xl font-bold leading-tight mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            CREATE<br />NEW<br />PASSWORD
          </h2>
          <p className="text-slate-300 text-base max-w-md leading-relaxed">
            Choose a strong password to keep your account secure.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="login-right">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, hsl(221, 83%, 53%), hsl(221, 83%, 42%))", boxShadow: "0 4px 14px rgba(37, 99, 235, 0.25)" }}>
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                SIMLABDT
              </h1>
              <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Manager</span>
            </div>
          </div>

          {!success ? (
            <>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <KeyRound className="w-6 h-6 text-blue-700" />
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                Set New Password
              </h3>
              <p className="text-slate-500 text-sm mb-8">
                Your new password must be different from previously used passwords.
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 text-sm" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="password" className="text-slate-700 text-xs uppercase tracking-wider font-bold mb-2 block">
                    New Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a strong password"
                    className="rounded-lg border-slate-300 focus:border-blue-500 focus:ring-blue-500/20 bg-slate-50 focus:bg-white h-11"
                    data-testid="reset-password-input"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-slate-700 text-xs uppercase tracking-wider font-bold mb-2 block">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    className="rounded-lg border-slate-300 focus:border-blue-500 focus:ring-blue-500/20 bg-slate-50 focus:bg-white h-11"
                    data-testid="reset-confirm-password-input"
                    required
                  />
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-500 font-medium mb-2">Password requirements:</p>
                  <ul className="text-xs text-slate-400 space-y-1">
                    <li className={password.length >= 12 ? 'text-emerald-600' : ''}>
                      {password.length >= 12 ? '✓' : '○'} At least 12 characters
                    </li>
                    <li className={/[A-Z]/.test(password) ? 'text-emerald-600' : ''}>
                      {/[A-Z]/.test(password) ? '✓' : '○'} One uppercase letter
                    </li>
                    <li className={/[a-z]/.test(password) ? 'text-emerald-600' : ''}>
                      {/[a-z]/.test(password) ? '✓' : '○'} One lowercase letter
                    </li>
                    <li className={/[0-9]/.test(password) ? 'text-emerald-600' : ''}>
                      {/[0-9]/.test(password) ? '✓' : '○'} One number
                    </li>
                  </ul>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary rounded-lg h-11 font-medium shadow-sm active:scale-[0.98] transition-transform"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                  {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                Password Reset!
              </h3>
              <p className="text-slate-500 text-sm mb-8">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>

              <Button
                onClick={() => navigate('/login')}
                className="w-full btn-primary rounded-lg h-11"
                data-testid="continue-to-login-btn"
              >
                Continue to Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          <div className="mt-8 flex items-center gap-2 text-[11px] text-slate-400">
            <Shield className="w-3.5 h-3.5" />
            <span>Secured password reset &middot; All sessions invalidated</span>
          </div>
        </div>
      </div>
    </div>
  );
}
